import type { Editor } from '../editor/editor';
import { api, ApiError } from '../sync/api';
import { finishLegacyImport, getEntries, getWords, legacyWordsToImport, setServerList, validateWord, type ListChange } from '../thai/dictionary';
import { thDateTime } from '../time';
import { $, $in } from './dom';
import { toast } from './toast';

/*
 * "คำห้ามตัดของกลุ่ม": one list for the whole group (src/thai/dictionary.ts, /api/words).
 * Everyone adds; a word is removed by whoever added it, or by an admin (the server checks this too).
 * When the list changed since this device last saw it, a bar over the work says what changed.
 */

let editorRef: Editor | null = null;
let me: () => string | undefined = () => undefined;
let canRemoveAll = false;
let offlineReason = '';
const mine = new Set<string>();      // words this person added/removed in this session: no "list changed" bar for those

const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
const needOnline = (e: unknown, what: string) =>
  e instanceof ApiError && e.status === 0 ? `ต้องออนไลน์จึงจะ${what}ได้ (${e.message})` : `${what}ไม่สำเร็จ: ${errText(e)}`;

function say(text: string, ok = false) {
  const m = $('dMsg');
  m.textContent = text; m.hidden = !text; m.classList.toggle('ok', ok);
}

/** Fetch the group list; rewrap text and show what others changed. Keeps the remembered list when offline. */
export async function syncWords(): Promise<void> {
  try {
    const r = await api.words();
    canRemoveAll = r.canRemoveAll; offlineReason = '';
    const change = setServerList(r.words);
    const others: ListChange = {
      added: change.added.filter(e => e.addedBy?.id !== me() && !mine.has(e.word)),
      removed: change.removed.filter(w => !mine.has(w))
    };
    mine.clear();
    if (change.added.length || change.removed.length) editorRef?.rewrapAll();
    if (others.added.length || others.removed.length) showChangeBar(others);
  } catch (e) {
    offlineReason = `ใช้รายการล่าสุดที่จำไว้ในเครื่อง เพราะโหลดรายการของกลุ่มไม่ได้: ${errText(e)}`;
  }
  renderDict();
}

function showChangeBar(c: ListChange) {
  const parts = [
    ...c.added.map(e => `${e.addedBy ? e.addedBy.name : 'ระบบ'} เพิ่ม "${e.word}" ${thDateTime(new Date(e.addedAt))}`),
    ...c.removed.map(w => `"${w}" ถูกลบ`)
  ];
  const shown = parts.slice(0, 3).join(' · ') + (parts.length > 3 ? ` และอีก ${parts.length - 3} รายการ` : '');
  $('wordsBarMsg').textContent = `รายการคำห้ามตัดของกลุ่มเปลี่ยน: ${shown} · ข้อความในงานอาจตัดบรรทัดใหม่`;
  $('wordsBar').hidden = false;
}

/** Add one word to the group list (from this panel or from the text edit bar). */
export async function addWordAndRefresh(ed: Editor, raw: string): Promise<boolean> {
  const err = validateWord(raw, getWords());
  if (err) {
    // say who added it when it's already there
    const w = raw.normalize('NFC').trim();
    const e = getEntries().find(x => x.word === w);
    const msg = e ? `"${w}" อยู่ในรายการแล้ว (${e.addedBy ? `เพิ่มโดย ${e.addedBy.name}` : 'ค่าเริ่มต้นของระบบ'} ${thDateTime(new Date(e.addedAt))})` : err;
    say(msg); toast(msg, true);
    return false;
  }
  try {
    const r = await api.addWord(raw);
    if (r.already) { say(r.message); toast(r.message, true); await syncWords(); return false; }
    mine.add(r.word.word);
    await syncWords();
    say(r.message, true); toast(r.message);
    ed.rewrapAll();
    return true;
  } catch (e) {
    const msg = needOnline(e, 'เพิ่มคำ');
    say(msg); toast(msg, true);
    return false;
  }
}

async function removeWord(w: string) {
  try {
    const r = await api.removeWord(w);
    mine.add(w);
    say(r.message, true);
  } catch (e) {
    const msg = needOnline(e, `ลบ "${w}"`);
    say(msg); toast(msg, true);
  }
  await syncWords();
}

function renderImport() {
  const box = $('dImport');
  const legacy = legacyWordsToImport();
  box.hidden = !legacy.length;
  if (!legacy.length) return;
  const list = legacy.slice(0, 12).join(', ') + (legacy.length > 12 ? ` และอีก ${legacy.length - 12} คำ` : '');
  $('dImportMsg').textContent = `พบคำในเครื่องนี้ ${legacy.length} คำที่ยังไม่อยู่ในรายการของกลุ่ม: ${list}`;
  $('dImportYes').textContent = `เพิ่มทั้ง ${legacy.length} คำเข้ากลุ่ม`;
}

export function renderDict() {
  renderImport();
  const off = $('dOffline');
  off.textContent = offlineReason; off.hidden = !offlineReason;
  const box = $('dList');
  box.textContent = '';
  const entries = getEntries();
  if (!entries.length) {
    // never fetched yet on this device: show the built-in words without details
    for (const w of getWords()) box.append(Object.assign(document.createElement('li'), { textContent: w }));
    return;
  }
  for (const e of [...entries].reverse()) {
    const li = document.createElement('li');
    const text = document.createElement('span'); text.className = 'uwho';
    const who = e.addedBy ? (e.addedBy.id === me() ? 'คุณ' : e.addedBy.name) : null;
    text.append(Object.assign(document.createElement('b'), { textContent: e.word }),
      Object.assign(document.createElement('small'), { textContent: who ? `${who} · ${thDateTime(new Date(e.addedAt))}` : 'ค่าเริ่มต้นของระบบ' }));
    li.append(text);
    if (canRemoveAll || (e.addedBy && e.addedBy.id === me())) {
      const x = document.createElement('button');
      x.className = 'btn danger'; x.textContent = 'ลบ';
      x.setAttribute('aria-label', `ลบคำ ${e.word}`);
      x.onclick = () => { x.disabled = true; void removeWord(e.word); };
      li.append(x);
    }
    box.append(li);
  }
}

export function initDictSheet(ed: Editor, opts: { myId: () => string | undefined }) {
  editorRef = ed;
  me = opts.myId;
  const input = $in('dIn'), add = $('dAdd') as HTMLButtonElement;
  add.onclick = async () => {
    add.disabled = true;
    if (await addWordAndRefresh(ed, input.value)) input.value = '';
    add.disabled = false;
  };
  input.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); add.click(); } });
  $('wordsBarClose').onclick = () => { $('wordsBar').hidden = true; };

  const yes = $('dImportYes') as HTMLButtonElement;
  yes.onclick = async () => {
    const legacy = legacyWordsToImport();
    if (!legacy.length) return;
    yes.disabled = true;
    try {
      const r = await api.importWords(legacy.slice(0, 200));
      r.added.forEach(w => mine.add(w));
      const parts = [`เพิ่มเข้ากลุ่มแล้ว ${r.added.length} คำ`];
      if (r.already.length) parts.push(`มีอยู่แล้ว ${r.already.length} คำ`);
      if (r.refused.length) parts.push(`ไม่ผ่าน ${r.refused.length} คำ: ${r.refused.map(x => `"${x.word}" (${x.reason})`).join(', ')}`);
      if (legacy.length > 200) parts.push(`ยังเหลือ ${legacy.length - 200} คำ กดอีกครั้งเพื่อเพิ่มต่อ`);
      else finishLegacyImport();
      say(parts.join(' · '), !r.refused.length);
      await syncWords();
    } catch (e) {
      say(needOnline(e, 'เพิ่มคำจากเครื่องนี้'));
    } finally { yes.disabled = false; }
  };
  $('dImportNo').onclick = () => { finishLegacyImport(); renderDict(); say('ไม่เพิ่มคำจากเครื่องนี้ และจะไม่ถามอีก'); };
}
